import React, { useEffect, useState, useMemo } from "react";
import MessengerLayout from "../../layout/MessengerLayout";
import KanbanBoard     from "../../components/projects/KanbanBoard";
import TaskListView    from "../../components/projects/TaskListView";
import TaskDrawer      from "../../components/projects/TaskDrawer";
import { toast }       from "../../components/ui/Toast";

const API  = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";
const authH = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}`, "Content-Type": "application/json" });
const card  = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";

const TABS = [
  { key: "mytasks",   label: "My Tasks"    },
  { key: "myprojects",label: "My Projects" },
  { key: "impact",    label: "Perf Impact" },
];

export default function ProjectsMessengerPage() {
  const [tab,          setTab]          = useState("mytasks");
  const [tasks,        setTasks]        = useState([]);
  const [projects,     setProjects]     = useState([]);
  const [impact,       setImpact]       = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskView,     setTaskView]     = useState("kanban");
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState(null);

  const me = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("user") || "null"); } catch { return null; }
  }, []);

  useEffect(() => {
    setLoading(true); setError(null);
    Promise.all([
      fetch(`${API}/api/projects/tasks/my`,     { headers: authH() }).then(r => r.json()),
      fetch(`${API}/api/projects/my`,           { headers: authH() }).then(r => r.json()),
      fetch(`${API}/api/projects/my/perf-impact`,{ headers: authH() }).then(r => r.json()),
    ])
      .then(([t, p, imp]) => {
        setTasks(Array.isArray(t) ? t : []);
        setProjects(Array.isArray(p) ? p : []);
        setImpact(imp || null);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const patchTask = async (id, patch) => {
    // Optimistic update — snapshot previous state for rollback on failure.
    let prevSnapshot;
    setTasks(prev => {
      prevSnapshot = prev;
      return prev.map(t => t.id === id ? { ...t, ...patch } : t);
    });
    try {
      const res = await fetch(`${API}/api/projects/tasks/${id}`, {
        method: "PATCH", headers: authH(), body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch (e) {
      // Roll back optimistic state
      if (prevSnapshot) setTasks(prevSnapshot);
      toast.error("Could not move task — changes reverted");
    }
  };

  /* ── Stats ── */
  const total      = tasks.length;
  const done       = tasks.filter(t => t.status === "Done").length;
  const inProg     = tasks.filter(t => t.status === "In Progress").length;
  const overdue    = tasks.filter(t => t.due_date && new Date(t.due_date) < new Date() && t.status !== "Done").length;
  const completion = total ? Math.round((done / total) * 100) : 0;

  return (
    <MessengerLayout>
      <div className="p-6 text-white space-y-5">

        <div>
          <h1 className="text-2xl font-bold text-[#00ff90]">My Projects & Tasks</h1>
          <p className="text-white/40 text-sm mt-0.5">Track your assignments, deadlines and performance impact</p>
        </div>

        {/* Personal KPIs */}
        <div className={`${card} p-5`}>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              { label: "My Tasks",   value: total,      color: "#00ff90" },
              { label: "In Progress",value: inProg,     color: "#60a5fa" },
              { label: "Completed",  value: done,       color: "#34d399" },
              { label: "Completion", value:`${completion}%`, color: "#fbbf24" },
              { label: "Overdue",    value: overdue,    color: overdue > 0 ? "#f87171" : "rgba(255,255,255,0.2)" },
            ].map(({ label, value, color }) => (
              <div key={label} className="rounded-lg border border-white/5 bg-white/3 p-3 text-center">
                {loading ? <div className="h-6 rounded bg-white/10 animate-pulse mb-1" /> : (
                  <p className="text-lg font-bold" style={{ color }}>{value}</p>
                )}
                <p className="text-xs text-white/30 mt-0.5">{label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-white/5 pb-1">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`text-xs px-4 py-2 rounded-t-lg border-b-2 transition-colors ${
                tab === t.key ? "border-[#00ff90] text-[#00ff90]" : "border-transparent text-white/40 hover:text-white/70"
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        {error && <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-red-300 text-sm">⚠ {error}</div>}

        {/* ── MY TASKS ── */}
        {tab === "mytasks" && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <button onClick={() => setTaskView("kanban")}
                className={`text-xs px-4 py-2 rounded-lg border transition-colors ${taskView === "kanban" ? "border-[#00ff90]/40 bg-[#00ff90]/10 text-[#00ff90]" : "border-white/10 text-white/40 hover:text-white"}`}>
                Kanban
              </button>
              <button onClick={() => setTaskView("list")}
                className={`text-xs px-4 py-2 rounded-lg border transition-colors ${taskView === "list" ? "border-[#00ff90]/40 bg-[#00ff90]/10 text-[#00ff90]" : "border-white/10 text-white/40 hover:text-white"}`}>
                List
              </button>
            </div>

            {taskView === "kanban" ? (
              <KanbanBoard
                tasks={tasks}
                members={me ? [{ id: me.id, name: me.name }] : []}
                onMove={(id, status) => patchTask(id, { status })}
                onOpen={setSelectedTask}
              />
            ) : (
              <TaskListView
                tasks={tasks}
                members={me ? [{ id: me.id, name: me.name }] : []}
                projects={projects}
                onOpen={setSelectedTask}
                onMove={(id, status) => patchTask(id, { status })}
              />
            )}
          </div>
        )}

        {/* ── MY PROJECTS ── */}
        {tab === "myprojects" && (
          <div className="space-y-4">
            {projects.length === 0 ? (
              <div className={`${card} p-8 text-center`}>
                <p className="text-white/20 text-sm italic">You're not assigned to any projects yet.</p>
              </div>
            ) : projects.map(p => {
              const myPTasks  = tasks.filter(t => t.project_id === p.id);
              const myDone    = myPTasks.filter(t => t.status === "Done").length;
              const myPct     = myPTasks.length ? Math.round((myDone / myPTasks.length) * 100) : 0;
              return (
                <div key={p.id} className={card}>
                  <div className="px-5 py-4 border-b border-white/5 flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-white/80">{p.name}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full border ${
                          p.status === "Active" ? "text-[#00ff90] bg-[#00ff90]/10 border-[#00ff90]/20" :
                          p.status === "Completed" ? "text-blue-400 bg-blue-500/10 border-blue-500/20" :
                          "text-white/30 bg-white/5 border-white/10"
                        }`}>{p.status}</span>
                      </div>
                      {p.description && <p className="text-xs text-white/30 mt-1">{p.description}</p>}
                      {p.end_date && <p className="text-xs text-white/25 mt-0.5">Due: {p.end_date}</p>}
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-xl font-bold text-[#00ff90]">{myPct}%</p>
                      <p className="text-xs text-white/25">My completion</p>
                    </div>
                  </div>

                  {/* My tasks in this project */}
                  <div className="p-4 space-y-2">
                    {myPTasks.length === 0 ? (
                      <p className="text-white/20 text-xs italic">No tasks assigned to you in this project.</p>
                    ) : myPTasks.map(t => {
                      const overdue = t.due_date && new Date(t.due_date) < new Date() && t.status !== "Done";
                      return (
                        <button key={t.id} onClick={() => setSelectedTask(t)}
                          className={`w-full text-left rounded-lg border px-4 py-3 hover:border-[#00ff90]/20 hover:bg-[#00ff90]/3 transition-all ${
                            t.status === "Done" ? "border-white/5 bg-white/3 opacity-50" :
                            overdue ? "border-red-500/20 bg-red-500/3" : "border-white/5 bg-white/3"
                          }`}>
                          <div className="flex items-center justify-between gap-2">
                            <p className={`text-sm font-medium ${t.status === "Done" ? "line-through text-white/30" : "text-white/75"}`}>
                              {t.title}
                            </p>
                            <div className="flex items-center gap-2 shrink-0">
                              {t.goal_id && <span className="text-xs text-[#00ff90]/40">⚡</span>}
                              <span className={`text-xs px-2 py-0.5 rounded-full border ${
                                t.status === "Done" ? "text-[#00ff90] bg-[#00ff90]/10 border-[#00ff90]/20" :
                                t.status === "In Progress" ? "text-yellow-400 bg-yellow-500/10 border-yellow-500/20" :
                                "text-white/30 bg-white/5 border-white/10"
                              }`}>{t.status}</span>
                            </div>
                          </div>
                          {t.due_date && (
                            <p className={`text-xs mt-1 ${overdue ? "text-red-400" : "text-white/25"}`}>
                              {overdue ? "Overdue · " : "Due: "}{t.due_date}
                            </p>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  {/* Progress bar */}
                  <div className="px-5 pb-4">
                    <div className="flex items-center gap-3">
                      <div className="flex-1 h-1.5 rounded-full bg-white/5">
                        <div className="h-full rounded-full bg-[#00ff90] transition-all" style={{ width: `${myPct}%` }} />
                      </div>
                      <span className="text-xs text-[#00ff90]/50">{myDone}/{myPTasks.length} done</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── PERF IMPACT ── */}
        {tab === "impact" && (
          <div className="space-y-4">

            {/* How it works */}
            <div className={`${card} p-5`}>
              <p className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium mb-3">How Task Completion Affects Your Performance Score</p>
              <div className="space-y-2 text-sm text-white/50">
                <p>Each task has a <span className="text-white/70">Performance Score Weight</span> (1–5). When you mark a task as Done, that weight is added to your performance contribution score for the current cycle.</p>
                <p>Tasks linked to a <span className="text-[#00ff90]/70">⚡ KRA/KPI goal or OKR</span> also update that goal's progress automatically.</p>
                <p>Your manager can see your task completion rate alongside your performance rating.</p>
              </div>
            </div>

            {/* Score breakdown */}
            {impact ? (
              <div className="space-y-4">
                {/* Summary chips */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { label: "Score Earned",     value: impact.score_earned  ?? "—", color: "#00ff90" },
                    { label: "Score Possible",   value: impact.score_possible ?? "—",color: "#60a5fa" },
                    { label: "Completion %",     value: impact.completion_pct != null ? `${impact.completion_pct}%` : "—", color: "#fbbf24" },
                    { label: "Goals Updated",    value: impact.goals_updated ?? "—", color: "#34d399" },
                  ].map(({ label, value, color }) => (
                    <div key={label} className={`${card} p-4 text-center`}>
                      <p className="text-xl font-bold" style={{ color }}>{value}</p>
                      <p className="text-xs text-white/30 mt-0.5">{label}</p>
                    </div>
                  ))}
                </div>

                {/* Goal linkage table */}
                {(impact.linked_goals || []).length > 0 && (
                  <div className={card}>
                    <div className="px-5 py-4 border-b border-white/5">
                      <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Linked Goals Progress</span>
                    </div>
                    <div className="divide-y divide-white/5">
                      {impact.linked_goals.map((g, i) => (
                        <div key={i} className="flex items-center gap-4 px-5 py-3">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-white/70 font-medium">{g.title}</p>
                            <p className="text-xs text-white/30">{g.method} · {g.tasks_done}/{g.tasks_total} tasks done</p>
                          </div>
                          <div className="flex items-center gap-3 shrink-0">
                            <div className="w-24 h-1.5 rounded-full bg-white/5">
                              <div className="h-full rounded-full bg-[#00ff90]" style={{ width: `${g.progress_pct || 0}%` }} />
                            </div>
                            <span className="text-xs text-[#00ff90]/60 w-8 text-right">{g.progress_pct || 0}%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* High-weight tasks not yet done */}
                {(impact.pending_high_weight || []).length > 0 && (
                  <div className={card}>
                    <div className="px-5 py-4 border-b border-yellow-500/15 bg-yellow-500/3">
                      <span className="text-xs text-yellow-400/70 uppercase tracking-widest font-medium">High-Impact Tasks Still Pending</span>
                    </div>
                    <div className="divide-y divide-white/5">
                      {impact.pending_high_weight.map((t, i) => (
                        <button key={i} onClick={() => setSelectedTask(t)}
                          className="w-full text-left flex items-center justify-between gap-3 px-5 py-3 hover:bg-white/3 transition-colors">
                          <div>
                            <p className="text-sm text-white/70">{t.title}</p>
                            <p className="text-xs text-white/30">{t.project_name} · Weight: {t.perf_score_weight}/5</p>
                          </div>
                          <span className="text-xs text-yellow-400 border border-yellow-500/20 bg-yellow-500/10 px-2 py-0.5 rounded-full shrink-0">
                            +{t.perf_score_weight} pts pending
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              loading ? (
                <div className={`${card} p-5`}><div className="h-24 rounded-lg bg-white/5 animate-pulse" /></div>
              ) : (
                <div className={`${card} p-8 text-center`}>
                  <p className="text-white/20 text-sm italic">No performance impact data yet. Complete some tasks to see your score.</p>
                </div>
              )
            )}
          </div>
        )}
      </div>

      <TaskDrawer
        open={!!selectedTask}
        task={selectedTask}
        members={me ? [{ id: me.id, name: me.name }] : []}
        projects={projects}
        onClose={() => setSelectedTask(null)}
        onSave={async (draft) => {
          patchTask(draft.id, draft);
          setSelectedTask(null);
        }}
        onMove={(id, status) => patchTask(id, { status })}
      />
    </MessengerLayout>
  );
}